import { ulid } from 'ulid'
import { validatePaymentPlan } from '../api/schemas'
import type { PaymentPlanKind } from '../api/response-types'

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
}

// The field-less tagged errors carry no text of their own, so the wording lives here. The tag
// stays in the copy: it is what ops quote when they escalate.
const TAGGED_ERROR_MESSAGES = new Map<string, string>([
  [
    'PlanGone',
    "This plan's workflow could not be reached, so nothing was applied. Reload the plan and try " +
      'once more - if it fails again the plan needs engineering to recover it. (PlanGone)',
  ],
  ['PlanNotFound', 'This plan no longer exists. (PlanNotFound)'],
  ['Forbidden', "You don't have permission to do this. (Forbidden)"],
  ['WorkflowConflict', "This plan's workflow has already been started. (WorkflowConflict)"],
  [
    'ServiceUnavailable',
    'The repayments service is temporarily unreachable - nothing was applied. Try again shortly. (ServiceUnavailable)',
  ],
])

// Untyped on purpose: a non-string `message`/`error` has to keep reaching the caller, where
// `errorMessage.toLowerCase()` throws on it exactly as it does today.
export const resolveApiError = (
  errorData: any,
  status: number,
  statusText: string,
): { errorMessage: any; statusCode: number } => {
  const errorMessage =
    // Match the tag, not the bare field: `reason` is also a request-payload field on every
    // command, so an echoed body would surface the operator's own justification as the error.
    (errorData?._tag === 'CommandRejected' && errorData.reason) ||
    TAGGED_ERROR_MESSAGES.get(errorData?._tag) ||
    errorData?.message ||
    errorData?.error ||
    `API request failed: ${status} ${statusText}`

  // Some BFF routes stringify the whole upstream response into their own `error` field, so the
  // real error and status have to be dug back out of that text.
  const embedded = typeof errorData?.error === 'string' ? errorData.error.match(/{.*}/) : null
  if (!embedded) return { errorMessage, statusCode: status }

  try {
    const nested = JSON.parse(embedded[0])
    const statusMatch = errorData.error.match(/status (\d+)/)
    return {
      errorMessage: nested.error || errorMessage,
      statusCode: statusMatch ? parseInt(statusMatch[1], 10) : status,
    }
  } catch {
    return { errorMessage, statusCode: status }
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Unauthorized: Session expired or invalid')
    this.name = 'SessionExpiredError'
  }
}

const reloadIfRestoredFromBackForwardCache = (event: PageTransitionEvent) => {
  if (event.persisted) window.location.reload()
}

export const redirectToLogin = () => {
  window.addEventListener('pageshow', reloadIfRestoredFromBackForwardCache)
  const returnTo = window.location.pathname + window.location.search
  window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`
}

async function apiRequest<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  data?: unknown,
  options?: {
    headers?: Record<string, string>
    signal?: AbortSignal
  },
): Promise<T> {
  let url = endpoint
  if (url.includes('?')) {
    const [path, query] = url.split('?')
    url = path + '?' + query
  }

  const requestOptions: RequestInit = {
    method,
    headers: {
      ...defaultHeaders,
      ...(options?.headers || {}),
    },
    credentials: 'include',
    signal: options?.signal,
  }

  if (data) {
    requestOptions.body = JSON.stringify(data)
  }

  const response = await fetch(url, requestOptions)
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)

    const { errorMessage, statusCode } = resolveApiError(errorData, response.status, response.statusText)

    if (statusCode === 401 || errorMessage.toLowerCase().includes('unauthorized')) {
      // This redirect happens below the store's catch, so an aborted request must be
      // checked here directly - by the time the store sees anything, navigation has begun.
      if (!options?.signal?.aborted) {
        redirectToLogin()
      }
      throw new SessionExpiredError()
    }

    throw new ApiError(errorMessage, statusCode)
  }

  return response.json()
}

interface User {
  id: string
  email: string
  provider: string
}

interface SessionResponse {
  authenticated: boolean
  user?: User
  message?: string
}

export interface Payment {
  id?: string
  amount: string
  status: string
  description: string
  date: string
  dateISO: string
  refundedAmount?: string | null
  amountMoney?: {
    amount: number
    currencyCode: string
  }
  refundedAmountMoney?: {
    amount: number
    currencyCode: string
  }
}

export interface Instalment {
  id: string
  date: string
  status: string
  amount: string
  outstanding?: {
    amount: number
    currencyCode: string
  }
}

interface Event {
  type: string
  description: string
  date: string
}

export interface Plan {
  paymentPlanId: string
  kind: PaymentPlanKind
  bookingReference: string
  workflowId: string
  assetSource: string
  assetId: string
  agentId: string
  customerId: string
  customerName: string
  customerEmail: string
  depositDate: string
  depositDateISO: string
  departureDate: string
  departureDateISO: string
  nextInstalment: string
  nextInstalmentDate: string
  nextInstalmentDateISO: string
  revision: string
  numberOfInstalments: number
  numberOfOutstandingInstalments: number
  numberOfOverdueInstalments: number
  status: string
  paymentMethodStatus?: 'valid' | 'halted'
  inCollections: boolean
  terminated: boolean
  total: string
  totalAmount: string
  totalAmountMoney: {
    amount: number
    currencyCode: string
  }
  depositAmount: string
  depositAmountMoney: {
    amount: number
    currencyCode: string
  }
  paidAmount: string
  paidAmountMoney: {
    amount: number
    currencyCode: string
  }
  adjustedAmount: string
  outstandingAmount: string
  outstandingAmountMoney: {
    amount: number
    currencyCode: string
  }
  pastDueAmount: string
  pastDueAmountMoney: {
    amount: number
    currencyCode: string
  }
  remainingAmount: string
  finalRepayment: string
  instalmentCount: number
  depositReceived: string
  payments: {
    upcoming: Payment[]
    history: Payment[]
  }
  events: {
    upcoming: Event[]
    completed: Event[]
  }
  instalments: Instalment[]
  amounts: {
    totalMoney: { amount: number; currencyCode: string }
    outstandingMoney: { amount: number; currencyCode: string }
  }
  id: string
  timezone?: string
}

interface PlansResponse {
  plans: Plan[]
  // Computed server-side before per-plan authorization filtering, so a page
  // can report a next page even when it returns fewer than `limit` rows.
  hasMore: boolean
  lastEvaluatedId?: string
  lastEvaluatedSort?: string
  filters: {
    filter: string
    sortOrder: string
    search?: string
    sortBy?: string
    limit?: number
  }
  pagination?: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

interface RefundPaymentPayload {
  workflowId: string
  paymentId: string
  reason: string
  amountToRefund: {
    amount: number
    currencyCode: string
  }
  recollection?: {
    shouldBeRecollected: boolean
    date?: string
  }
  category: string
}

interface SkipPaymentPayload {
  reason: string
}

interface ReschedulePaymentPayload {
  newDate: string
  reason: string
}

interface TerminatePlanPayload {
  reason: string
}

interface SuspendPaymentsPayload {
  workflowId: string
  reason: string
}

interface ReopenPlanPayload {
  reason: string
}

interface ClearHaltByOperatorPayload extends Record<string, unknown> {
  reason: string
}

interface ResumePaymentsPayload {
  workflowId: string
  reason: string
  newFrequency: string
  remainingInstalmentCount: number
  nextInstalmentDate: string
}

interface AdjustPlanTotalPayload {
  workflowId: string
  newTotalAmount: {
    currencyCode: string
    amount: number
  }
  adjustmentType: string
  reason: string
}

interface InstalmentPaidOutOfBandPayload {
  workflowId: string
  paymentMethod:
    | {
        '!': 'out-of-band:bank-transfer'
        referenceNumber: string
      }
    | {
        '!': 'out-of-band:stripe-invoice'
        stripeInvoiceId: string
        stripeAccountId?: string
      }
  date: string
  reason: string
}

interface OffsetDatesPayload {
  workflowId: string
  reason: string
  offsetInDays: number
}

interface ChangeInstalmentDatePayload {
  workflowId: string
  reason: string
  newDate: string
}

interface ChangeTimezonePayload {
  workflowId: string
  reason: string
  newTimezone: string
}

interface ChargeOverdueBalancePayload {
  workflowId: string
  reason: string
}

interface ProcessInstalmentPayload {
  workflowId: string
  reason: string
}

interface ProcessManualPaymentPayload {
  workflowId: string
  amount: {
    currencyCode: string
    amount: number
  }
  adjustmentType: string
  reason: string
}

interface RecalculateFrequencyPayload {
  workflowId: string
  reason: string
  frequency: string
  instalmentCount: number
  nextInstalmentDate: string
}

interface PaymentReceivedOutOfBandPayload {
  workflowId: string
  paymentMethod: {
    '!': string
    referenceNumber?: string
    stripeInvoiceId?: string
  }
  date: string
  amount: {
    amount: number
    currencyCode: string
  }
  reason: string
  adjustmentType: string
}

interface ApiResponse<T = void> {
  success: boolean
  data?: T
  message?: string
}

interface PlansOptions {
  limit?: number
  search?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  last_evaluated_id?: string
  last_evaluated_sort?: string
  signal?: AbortSignal
}

export const api = {
  getSession: () => apiRequest<SessionResponse>('/api/session'),

  getPlans: (status?: string, options?: PlansOptions) => {
    const queryParams = new URLSearchParams()
    if (status && status !== 'all') {
      queryParams.append('filter', status)
    }

    let signal: AbortSignal | undefined
    if (options) {
      const { signal: optionsSignal, ...queryOptions } = options
      signal = optionsSignal

      if (queryOptions.limit) {
        queryParams.append('limit', queryOptions.limit.toString())
      }

      Object.entries(queryOptions).forEach(([key, value]) => {
        if (key !== 'limit' && key !== 'filter' && value !== undefined) {
          const stringValue = value.toString()
          queryParams.append(key, stringValue)
        }
      })
    }

    const queryString = queryParams.toString()
    return apiRequest<PlansResponse>(`/api/plans?${queryString}`, 'GET', undefined, { signal })
  },

  getPlanById: async (paymentPlanId: string) => {
    const response = await apiRequest<{ plan: unknown }>(`/api/plan/${paymentPlanId}`)
    const validated = validatePaymentPlan(response.plan)
    if (!validated) {
      throw new Error('Invalid plan data received from server')
    }
    return validated
  },

  refundPayment: (paymentPlanId: string, data: RefundPaymentPayload, idempotencyKey: string) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/refund`, 'POST', data, {
      headers: {
        'x-idempotency-key': idempotencyKey,
      },
    }),

  skipPayment: (paymentPlanId: string, paymentId: string, data: SkipPaymentPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/instalment/${paymentId}/skip`, 'POST', data),

  reschedulePayment: (paymentPlanId: string, paymentId: string, data: ReschedulePaymentPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/instalment/${paymentId}/reschedule`, 'POST', data),

  terminatePlan: (paymentPlanId: string, data: TerminatePlanPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/terminate`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  completePlan: (paymentPlanId: string) =>
    apiRequest<ApiResponse>(
      `/api/plan/${paymentPlanId}/complete`,
      'POST',
      {},
      {
        headers: {
          'x-idempotency-key': ulid(),
        },
      },
    ),

  suspendPayments: (paymentPlanId: string, data: SuspendPaymentsPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/suspend-payments`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  reactivatePlan: (paymentPlanId: string, data: ResumePaymentsPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/resume-payments`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  adjustPlanTotal: (paymentPlanId: string, data: AdjustPlanTotalPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/adjust-total`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  instalmentPaidOutOfBand: (paymentPlanId: string, instalmentId: string, data: InstalmentPaidOutOfBandPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/instalment/${instalmentId}/out-of-band-payment`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  offsetDates: (paymentPlanId: string, data: OffsetDatesPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/offset-dates`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  changeInstalmentDate: (paymentPlanId: string, instalmentId: string, data: ChangeInstalmentDatePayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/instalment/${instalmentId}/change-date`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  changeTimezone: (paymentPlanId: string, data: ChangeTimezonePayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/change-timezone`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  chargeOverdueBalance: (paymentPlanId: string, data: ChargeOverdueBalancePayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/charge-overdue-balance`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  processInstalment: (paymentPlanId: string, instalmentId: string, data: ProcessInstalmentPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/instalment/${instalmentId}/pay`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  processManualPayment: (paymentPlanId: string, data: ProcessManualPaymentPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/pay`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  changeFrequency: (paymentPlanId: string, data: RecalculateFrequencyPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/change-frequency`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  paymentReceivedOutOfBand: (paymentPlanId: string, data: PaymentReceivedOutOfBandPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/out-of-band-payment`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  searchUnavailablePlan: (assetId: string) => {
    const queryParams = new URLSearchParams({ assetId })
    return apiRequest<{ success: boolean; paymentPlanId?: string }>(
      `/api/plans/search-unavailable?${queryParams.toString()}`,
      'GET',
    )
  },

  reopenPlan: (paymentPlanId: string, data: ReopenPlanPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/reopen`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),

  /**
   * "Retry again": clear an invalid-payment-method halt. Reopens the scheduler
   * gate so the next overdue cycle retries (re-halting if still unretryable).
   */
  clearHaltByOperator: (paymentPlanId: string, data: ClearHaltByOperatorPayload) =>
    apiRequest<ApiResponse>(`/api/plan/${paymentPlanId}/payment-method-halt/clear-by-operator`, 'POST', data, {
      headers: {
        'x-idempotency-key': ulid(),
      },
    }),
}

export default api
